import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DetailsModal from '@/components/DetailsModal';
import { ArrowUpRight, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// UTM dropdown options with column header labels
const UTM_OPTIONS = [
  { value: 'utm_mediums', label: 'UTM mediums', columnHeader: 'Medium' },
  { value: 'utm_sources', label: 'UTM sources', columnHeader: 'Source' },
  { value: 'utm_campaigns', label: 'UTM campaigns', columnHeader: 'Campaign' },
  { value: 'utm_contents', label: 'UTM contents', columnHeader: 'Content' },
  { value: 'utm_terms', label: 'UTM terms', columnHeader: 'Term' },
];

// Column headers for non-UTM tabs
const TAB_COLUMN_HEADERS = {
  'channels': 'Channel',
  'sources': 'Source',
};

const TrafficInsights = ({ websiteId, period = '24h', startDate, endDate, channel, location, device, os, browser, token, existingFilters = [] }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auth header helper — token is null/undefined when AnalyticsPage is rendered
  // in publicMode (via /site/:domain). The backend analytics endpoints accept
  // unauthenticated requests for public sites via get_current_user_optional,
  // so we simply omit the Authorization header in that case.
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  // Create stable filter strings to prevent infinite re-renders
  const filtersKey = useMemo(() => existingFilters.join('|'), [existingFilters]);
  
  // Ref to track if we're currently fetching to prevent duplicate calls
  const fetchingRef = useRef(false);
  const lastFetchKeyRef = useRef('');
  
  // State for UTM dropdown
  const [utmDropdownOpen, setUtmDropdownOpen] = useState(false);
  const [selectedUtm, setSelectedUtm] = useState('utm_mediums');
  const utmDropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (utmDropdownRef.current && !utmDropdownRef.current.contains(event.target)) {
        setUtmDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Handle channel click to filter dashboard - using unified ?f= format
  const handleChannelClick = (channelName) => {
    // Build URL with new unified filter format
    let url = `/analytics/${websiteId}?period=${period}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    
    // Keep existing f filters (existingFilters is array of raw filter strings like "is,os,Linux")
    existingFilters.forEach(f => {
      url += `&f=${encodeURIComponent(f)}`;
    });
    
    // Add new channel filter in unified format
    url += `&f=is,channel,${encodeURIComponent(channelName)}`;
    
    navigate(url);
  };
  
  // Handle source click to filter dashboard - using unified ?f= format
  const handleSourceClick = (sourceName) => {
    let url = `/analytics/${websiteId}?period=${period}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    
    // Keep existing f filters
    existingFilters.forEach(f => {
      url += `&f=${encodeURIComponent(f)}`;
    });
    
    // Add new source filter in unified format
    url += `&f=is,source,${encodeURIComponent(sourceName)}`;
    
    navigate(url);
  };
  
  // Handle UTM item click to filter dashboard - using unified ?f= format
  const handleUtmClick = (utmValue, utmType) => {
    let url = `/analytics/${websiteId}?period=${period}`;
    if (startDate && endDate) {
      url += `&start_date=${startDate}&end_date=${endDate}`;
    }
    
    // Keep existing f filters
    existingFilters.forEach(f => {
      url += `&f=${encodeURIComponent(f)}`;
    });
    
    // Map UTM type to filter key
    const filterKeyMap = {
      'utm_mediums': 'utm_medium',
      'utm_sources': 'utm_source',
      'utm_campaigns': 'utm_campaign',
      'utm_contents': 'utm_content',
      'utm_terms': 'utm_term',
    };
    
    const filterKey = filterKeyMap[utmType] || 'utm_campaign';
    url += `&f=is,${filterKey},${encodeURIComponent(utmValue)}`;
    
    navigate(url);
  };
  const [activeTab, setActiveTab] = useState('channels');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [limit, setLimit] = useState(9); // Default to top 3
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'visitors', direction: 'desc' });
  const [totalCount, setTotalCount] = useState(0);
  
  // Get page filters from URL
  const pageFilters = searchParams.getAll('f');
  
  // Create stable key for page filters to prevent infinite re-renders
  const pageFiltersKey = useMemo(() => pageFilters.sort().join('|'), [pageFilters.length, ...pageFilters]);

  // Format numbers: 57000 → "57k", 1234 → "1.2k"
  const formatNumber = (num) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
  };
  
  // Determine the actual endpoint to fetch based on activeTab
  // Using useMemo to ensure stable reference
  const currentEndpoint = useMemo(() => {
    if (activeTab === 'utm') {
      return selectedUtm; // utm_mediums, utm_sources, etc.
    }
    return activeTab; // channels, sources
  }, [activeTab, selectedUtm]);

  // Fetch data when tab changes
  useEffect(() => {
    const fetchData = async () => {
      // Public viewers have no token but the backend allows unauthenticated reads
      // for is_public=true sites — only bail when we have no website to query.
      if (!websiteId) return;
      
      // Create a unique key for this fetch request using currentEndpoint
      const fetchKey = `${currentEndpoint}-${websiteId}-${period}-${startDate}-${endDate}-${channel}-${location}-${device}-${os}-${browser}-${pageFiltersKey}`;
      
      // Skip if same request
      if (lastFetchKeyRef.current === fetchKey) {
        return;
      }
      
      // If currently fetching, skip to prevent race conditions
      if (fetchingRef.current) {
        return;
      }
      
      fetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;

      setLoading(true);
      setError(null);

      try {
        // Build URL with period and optional custom dates
        let url = `${BACKEND_URL}/api/analytics/${websiteId}/${currentEndpoint}?period=${period}`;
        
        if (startDate && endDate) {
          url += `&start_date=${startDate}&end_date=${endDate}`;
        }
        if (channel) {
          url += `&channel=${encodeURIComponent(channel)}`;
        }
        if (location) {
          url += `&location=${encodeURIComponent(location)}`;
        }
        if (device) {
          url += `&device=${encodeURIComponent(device)}`;
        }
        if (os) {
          url += `&os=${encodeURIComponent(os)}`;
        }
        if (browser) {
          url += `&browser=${encodeURIComponent(browser)}`;
        }
        // Add page filters
        pageFilters.forEach(f => {
          url += `&f=${encodeURIComponent(f)}`;
        });
        
        const response = await axios.get(url, { headers: authHeaders });
        setData(response.data.items || []);
        setTotalCount(response.data.total || response.data.items?.length || 0);
      } catch (err) {
        console.error('Error fetching traffic data:', err);
        setError('Failed to load data');
        setData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchData();
  }, [currentEndpoint, websiteId, period, startDate, endDate, channel, location, device, os, browser, token, pageFiltersKey]);
  
  // Fetch all data for modal when it opens
  useEffect(() => {
    const fetchModalData = async () => {
      // Same publicMode tolerance as the main fetch — see note on fetchData above.
      if (!showModal || !websiteId) return;

      setModalLoading(true);

      try {
        // The Sources modal pulls the RICH /details?type=sources endpoint which
        // returns friendly name + favicon + real bounce_rate + visit_duration
        // (Plausible's expanded "Top sources" view). Other tabs keep the simple
        // per-tab endpoint (channels/utm don't have those extra columns).
        const isSources = activeTab === 'sources';
        let url = isSources
          ? `${BACKEND_URL}/api/analytics/${websiteId}/details?type=sources&tab=top&period=${period}`
          : `${BACKEND_URL}/api/analytics/${websiteId}/${currentEndpoint}?period=${period}`;
        if (startDate && endDate) {
          url += `&start_date=${startDate}&end_date=${endDate}`;
        }
        if (channel) {
          url += `&channel=${encodeURIComponent(channel)}`;
        }
        if (location) {
          url += `&location=${encodeURIComponent(location)}`;
        }
        if (device) {
          url += `&device=${encodeURIComponent(device)}`;
        }
        if (os) {
          url += `&os=${encodeURIComponent(os)}`;
        }
        if (browser) {
          url += `&browser=${encodeURIComponent(browser)}`;
        }

        // Pass unified f filters (existingFilters is array of raw filter strings)
        if (existingFilters && existingFilters.length > 0) {
          existingFilters.forEach(f => {
            url += `&f=${encodeURIComponent(f)}`;
          });
        }

        const response = await axios.get(url, { headers: authHeaders });
        if (isSources) {
          // /details returns rich records already in the shape the modal needs.
          setModalData(response.data.records || []);
        } else {
          // Other tabs: transform the simple {name, visitors} items.
          const items = response.data.items || [];
          setModalData(items.map(item => ({
            referrer: item.name,
            visitors: item.visitors,
            pageviews: item.visitors,
            bounce_rate: 0,
          })));
        }
      } catch (err) {
        console.error('Error fetching modal data:', err);
        setModalData([]);
      } finally {
        setModalLoading(false);
      }
    };

    fetchModalData();
  }, [showModal, activeTab, websiteId, period, startDate, endDate, channel, location, device, os, browser, token, filtersKey]);
  
  // Get limited data for display
  const displayData = data.slice(0, limit);
  
  // Filter and sort modal data
  const filteredModalData = modalData.filter(item => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return Object.values(item).some(value => {
      if (typeof value === 'string') {
        return value.toLowerCase().includes(query);
      }
      return false;
    });
  });
  
  const sortedModalData = [...filteredModalData].sort((a, b) => {
    const aVal = a[sortConfig.key];
    const bVal = b[sortConfig.key];
    
    if (typeof aVal === 'number' && typeof bVal === 'number') {
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    }
    
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    
    if (sortConfig.direction === 'asc') {
      return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
    } else {
      return aStr > bStr ? -1 : aStr < bStr ? 1 : 0;
    }
  });
  
  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  // Get icon for source
  // Generic "direct / none" glyph (no real site behind it).
  const directIcon = (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  );

  const getSourceIcon = (name, favicon) => {
    const lowerName = (name || '').toLowerCase();

    // Real favicon when the backend supplied a domain (Sources tab). Uses the
    // DuckDuckGo icon service (same one Plausible uses). Falls back to the
    // generic globe on load error so a missing icon never breaks the row.
    if (favicon && !lowerName.includes('direct') && !lowerName.includes('none')) {
      return (
        <img
          src={`https://icons.duckduckgo.com/ip3/${favicon}.ico`}
          alt=""
          width={16}
          height={16}
          loading="lazy"
          style={{ borderRadius: '3px', display: 'block' }}
          onError={(e) => {
            // Swap to a neutral globe glyph if the favicon can't load.
            e.currentTarget.style.display = 'none';
            if (e.currentTarget.nextSibling) e.currentTarget.nextSibling.style.display = 'block';
          }}
        />
      );
    }

    if (lowerName.includes('direct') || lowerName.includes('none')) {
      return directIcon;
    }

    // Channels / UTM tabs (no favicon) — neutral globe.
    return (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    );
  };

  return (
    <>
      <div className="data-table-container">
        <div className="table-header">
          <h3 className="table-title">Top Sources</h3>
          <div className="table-tabs">
            <button
              className={`table-tab ${activeTab === 'channels' ? 'active' : ''}`}
              onClick={() => setActiveTab('channels')}
              data-testid="tab-channels"
            >
              Channels
            </button>
            <button
              className={`table-tab ${activeTab === 'sources' ? 'active' : ''}`}
              onClick={() => setActiveTab('sources')}
              data-testid="tab-sources"
            >
              Sources
            </button>
            {/* UTM Dropdown - Plausible style */}
            <div className="relative" ref={utmDropdownRef}>
              <button
                className={`table-tab ${activeTab === 'utm' ? 'active' : ''}`}
                onClick={() => {
                  if (activeTab !== 'utm') {
                    setActiveTab('utm');
                  }
                  setUtmDropdownOpen(!utmDropdownOpen);
                }}
                data-testid="tab-utm-dropdown"
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {activeTab === 'utm' 
                  ? UTM_OPTIONS.find(o => o.value === selectedUtm)?.label || 'UTM mediums'
                  : 'Campaigns'
                }
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {/* Dropdown Menu */}
              {utmDropdownOpen && (
                <div 
                  className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[160px]"
                  style={{ 
                    animation: 'fadeIn 0.15s ease-out'
                  }}
                >
                  {UTM_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors ${
                        selectedUtm === option.value && activeTab === 'utm' 
                          ? 'bg-gray-100 font-medium' 
                          : ''
                      }`}
                      onClick={() => {
                        // Reset fetch key first to force refetch
                        lastFetchKeyRef.current = '';
                        // Then update states
                        setSelectedUtm(option.value);
                        setActiveTab('utm');
                        setUtmDropdownOpen(false);
                      }}
                      data-testid={`utm-option-${option.value}`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      
      <div className="table-content" style={{ minHeight: '300px' }}>
        {/* Column Headers */}
        {!loading && !error && displayData.length > 0 && (
          <div className="table-row" style={{ 
            borderBottom: '1px solid #e5e7eb',
            padding: '8px 16px',
            backgroundColor: '#f9fafb'
          }}>
            <div className="table-cell" style={{ 
              fontSize: '11px', 
              fontWeight: '600', 
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              flex: 1
            }}>
              {activeTab === 'utm' 
                ? UTM_OPTIONS.find(o => o.value === selectedUtm)?.columnHeader || 'Value'
                : TAB_COLUMN_HEADERS[activeTab] || 'Name'
              }
            </div>
            <div className="table-cell" style={{ 
              fontSize: '11px', 
              fontWeight: '600', 
              color: '#6b7280',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              textAlign: 'right',
              width: '80px'
            }}>
              Visitors
            </div>
          </div>
        )}
        
        {loading && (
          <div className="space-y-3 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-16"></div>
              </div>
            ))}
          </div>
        )}

        {error && !loading && (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-2">⚠️ {error}</div>
            <button
              className="text-blue-600 hover:underline text-sm"
              onClick={() => setActiveTab(activeTab)}
            >
              Try again
            </button>
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="table-row">
            <div className="table-cell">
              <span className="no-data">No {activeTab === 'utm' ? selectedUtm.replace('utm_', 'UTM ') : activeTab} data available</span>
            </div>
          </div>
        )}

        {!loading && !error && displayData.length > 0 && (
          <>
            {displayData.map((item, idx) => {
              // Determine if this row is clickable and which handler to use
              const isClickable = ['channels', 'sources', 'utm'].includes(activeTab);
              const handleClick = () => {
                if (activeTab === 'channels') {
                  handleChannelClick(item.name);
                } else if (activeTab === 'sources') {
                  handleSourceClick(item.name);
                } else if (activeTab === 'utm') {
                  handleUtmClick(item.name, selectedUtm);
                }
              };

              return (
                <div
                  key={idx}
                  className={`table-row traffic-row ${isClickable ? 'clickable-row' : ''}`}
                  data-testid={`${activeTab}-${item.name}`}
                  style={{
                    transition: 'background-color 0.2s',
                    cursor: isClickable ? 'pointer' : 'default'
                  }}
                  onClick={isClickable ? handleClick : undefined}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <div className="table-cell location-cell" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px',
                      color: '#6b7280'
                    }}>
                      {getSourceIcon(item.name, item.favicon)}
                      {/* Hidden fallback globe revealed by getSourceIcon's onError */}
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" style={{ display: 'none' }}>
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <span style={{ fontWeight: '400', color: '#0f172a' }}>{item.name}</span>
                  </div>
                  <div className="table-cell number-cell" style={{ textAlign: 'right', paddingRight: '16px' }}>
                    <span style={{
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#111827'
                    }}>
                      {formatNumber(item.visitors)}
                    </span>
                    <span style={{
                      fontSize: '12px',
                      color: '#10b981',
                      marginLeft: '8px'
                    }}>
                      ↗
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {totalCount > limit && (
        <button className="table-details" onClick={() => setShowModal(true)}>
          ↻ DETAILS
        </button>
      )}
    </div>
    
    {/* Details Modal */}
    <DetailsModal
      isOpen={showModal}
      onClose={() => {
        setShowModal(false);
        setSearchQuery('');
        setSortConfig({ key: 'visitors', direction: 'desc' });
      }}
      websiteId={websiteId}
      period={period}
      startDate={startDate}
      endDate={endDate}
      channel={channel}
      location={location}
      device={device}
      os={os}
      browser={browser}
      title={`Top ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} - All Results`}
      type="sources"
      data={sortedModalData}
      loading={modalLoading}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      sortConfig={sortConfig}
      onSort={handleSort}
      activeTab={activeTab}
    />
  </>
  );
};

export default TrafficInsights;
